import { useLazyQuery, useMutation } from "@apollo/client";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  COMPANY_ADMIN,
  FETCH_POLICY,
  KENDO_DATE_FORMAT,
  MIN_RESIZABLE_WIDTH,
  TABLE_PAGESIZE,
} from "src/constants";
import { getCompanyId, getUserId, getUserRole } from "src/utils";
import {
  checkinTemplates,
  previewCheckinTemplates,
} from "GraphQL/queries/listCheckins";
import { myCheckinTemplateState } from "Recoil/CheckinTemplate/atom";
import { GridColumn as Column } from "@progress/kendo-react-grid";
import { Box, Popover, Tooltip, Typography } from "@material-ui/core";
import UserProfileWidget from "Components/widgets/UserProfileWidget";
import GenericActionPopover from "Components/formControls/ActionsDropdown/GenericActionPopover";
import { CHECKIN_TYPE } from "Modules/Reports/constants";
import { get, orderBy } from "lodash";
import moment from "moment";
import { useFormContext } from "react-hook-form";
import CustomColumnSelector from "Components/GridTable/customColumnSelector";
import CustomColumnMenu from "Components/GridTable/customColumnMenu";
import { getGridColumns } from "src/utils/general";
import FilterIcon from "Assets/images/grid-filter.svg";
import { selectedFilterState } from "Recoil/KendoGridFilters/atom";
import { updateGridFilterMutation } from "GraphQL/mutation/gridFilters";
import omitDeep from "omit-deep-lodash";
import { UserGeneric } from "Modules/User/UserListing/ColumnListing";
import { previewCheckinTemplateState } from "Recoil/CheckinTemplate/atom";
import CreatedBy from "Modules/Checkins/TemplateList/Columns/CreatedBy";
import AssignedToTargets from "Modules/Checkins/TemplateList/Columns/AssignedToTargets";

export const useCheckinTemplates = (methods) => {
  const [myCheckinTemplate, setMyCheckinTemplate] = useRecoilState(
    myCheckinTemplateState
  );
  const setPreviewCheckinTemplate = useSetRecoilState(
    previewCheckinTemplateState
  );

  const name = "addCheckin";
  const { setValue } = { ...methods };
  const [selectedFilter, setSelectedFilter] =
    useRecoilState(selectedFilterState);
  const [updateFilterMutation] = useMutation(updateGridFilterMutation);

  const menuList = (dataItem, setOpenPreview) => {
    const userRole = getUserRole();
    const allMenu = [
      {
        type: "detail",
        isLink: false,
        label: "Preview template",
        onClick: (e) => {
          setOpenPreview({
            view: true,
            checkin: { ...get(dataItem, "dataItem", {}) },
          });
        },
      },
    ];

    if (
      get(dataItem, "dataItem.createdByUserId", "") === getUserId() ||
      userRole === COMPANY_ADMIN
    ) {
      allMenu.push({
        type: "edit",
        isLink: false,
        label: "Edit template",
        onClick: () => {
          editActionHandler(dataItem);
        },
      });
    }
    allMenu.push({
      type: "duplicate",
      isLink: false,
      label: "Duplicate template",
      onClick: () => duplicateActionHandler(dataItem),
    });
    if (
      get(dataItem, "dataItem.createdByUserId", "") === getUserId() ||
      userRole === COMPANY_ADMIN
    ) {
      allMenu.push({
        type: "delete",
        isLink: false,
        label: "Delete template",
        onClick: () => {
          deleteActionHandler(dataItem);
        },
      });
    }
    return allMenu;
  };
  const duplicateActionHandler = (checkin) => {
    get(checkin, "dataItem.id", "") &&
      setValue(`${name}.current`, {
        title: get(checkin, "dataItem.checkInType", ""),
        type: get(checkin, "dataItem.originalCheckInType", ""),
        action: "duplicate",
        id: get(checkin, "dataItem.id", ""),
        targetMode: "createCheckinTemplate",
        checkinMode: "edit",
        checkinModal: true,
      });
  };
  const deleteActionHandler = (checkin) => {
    setValue(`${name}.current.action`, "delete");
    setValue(`${name}.current.id`, get(checkin, "dataItem.id", ""));
  };
  const editActionHandler = (checkin) => {
    if (get(checkin, "dataItem.checkInType", "")) {
      setValue(`${name}.current`, {
        title: get(checkin, "dataItem.checkInType", ""),
        type: get(checkin, "dataItem.originalCheckInType", ""),
        id: get(checkin, "dataItem.id", ""),
        targetMode: "createCheckinTemplate",
        checkinMode: "edit",
        checkinModal: true,
      });
    }
  };

  const myCheckinTemplateColumns = [
    {
      srNo: 1,
      title: "Title",
      field: "title",
      key: "title",
      type: "string",
      show: true,
      minWidth: 500,
      minResizableWidth: MIN_RESIZABLE_WIDTH,
      filter: "text",
      sortable: true,
      isExportable: true,
      isRequired: true,
    },
    {
      srNo: 2,
      title: "Type",
      field: "checkInType",
      key: "checkInType",
      type: "string",
      minWidth: 160,
      minResizableWidth: MIN_RESIZABLE_WIDTH,
      show: true,
      filter: "text",
      sortable: true,
      isExportable: true,
      isRequired: true,
    },
    {
      srNo: 3,
      title: "Created by",
      field: "fullName",
      key: "fullName",
      type: "string",
      show: true,
      minWidth: 160,
      minResizableWidth: MIN_RESIZABLE_WIDTH,
      filter: "text",
      sortable: true,
      isExportable: true,
      isRequired: false,
    },
    {
      srNo: 4,
      title: "Created date",
      field: "createdAt",
      key: "createdAt",
      show: true,
      filter: "date",
      type: "date",
      minWidth: 160,
      minResizableWidth: MIN_RESIZABLE_WIDTH,
      format: `{0: MM/DD/YYYY}`,
      sortable: true,
      isExportable: true,
      isRequired: false,
    },
    {
      srNo: 5,
      title: "Assigned to targets",
      field: "assignedToTargets",
      key: "assignedToTargets",
      type: "number",
      show: true,
      filter: "text",
      minWidth: 160,
      minResizableWidth: MIN_RESIZABLE_WIDTH,
      sortable: true,
      isExportable: true,
      isRequired: false,
    },
    {
      srNo: 6,
      title: "Action",
      field: "action",
      key: "action",
      show: true,
      minWidth: 100,
      minResizableWidth: MIN_RESIZABLE_WIDTH,
      isExportable: true,
      isRequired: true,
    },
  ];

  const onColumnsSubmit = (_columns, gridFilters, setGridFilters) => {
    const queryVariables = {
      input: {
        ...selectedFilter,
        columns: getGridColumns(_columns),
        dataState: JSON.stringify(get(selectedFilter, "dataState", {})),
      },
    };
    updateFilterMutation({
      variables: omitDeep(queryVariables, "__typename", "key", "text"),
    }).then(({ data }) => {
      const _filters = gridFilters
        ? gridFilters.map((item) => {
            return item.filterName ===
              get(queryVariables, "input.filterName") &&
              item.id === get(queryVariables, "input.id")
              ? {
                  ...item,
                  columns: get(queryVariables, "input.columns"),
                }
              : item;
          })
        : [];
      const updatedFilter = _filters.find(
        (c) =>
          c.filterName === get(queryVariables, "input.filterName") &&
          c.id === get(queryVariables, "input.id")
      );
      setGridFilters(_filters);
      setSelectedFilter({ ...updatedFilter });
    });
  };

  const headerCell = (props) => (
    <Box
      display={"flex"}
      justifyContent={props?.align ? props?.align : "center"}
    >
      <span className={"k-link"} onClick={props.onClick}>
        {get(props, "title", "")}
        {props?.headerClassName ? (
          <img src={FilterIcon} alt="filter" className="ml10 v-middle" />
        ) : (
          ""
        )}
        {props.children}
      </span>
    </Box>
  );

  const individualFetchColumn = (
    column,
    data,
    setExpandedColumn,
    setColumnsState,
    gridFilters,
    setGridFilters,
    setKendoGridView,
    setOpenPreview,
    kendoGridView
  ) => {
    let columnObject = { ...column };
    const headerClassName = kendoGridView?.dataState?.filter?.filters?.find(
      ({ filters }) => filters?.[0]?.field == column.field
    )
      ? "active"
      : "";
    columnObject.className = headerClassName;

    let columnMenu = {
      columnMenu: (props) => (
        <CustomColumnMenu
          {...props}
          selectedFilter={{}}
          columns={myCheckinTemplateColumns}
          onColumnsSubmit={() => {}}
          data={data}
          setExpandedColumn={setExpandedColumn}
        />
      ),
    };
    // console.log('::',column)
    switch (column.field) {
      case "title":
        columnObject = {
          ...column,
          headerCell: (e) =>
            headerCell({ ...e, headerClassName, align: "left" }),
          data,
          cell: ({ dataItem }) => (
            <UserGeneric
              title={dataItem.title}
              onRowClick={() => {
                setOpenPreview({
                  view: true,
                  checkin: { ...dataItem },
                });
              }}
            />
          ),
        };
        break;
      case "checkInType":
        columnObject = {
          ...column,
          headerCell: (e) =>
            headerCell({ ...e, headerClassName, align: "left" }),
          data,
          cell: ({ dataItem }) => (
            <UserGeneric title={get(dataItem, "checkInType", "")} />
          ),
        };
        break;
      case "fullName":
        columnObject = {
          ...column,
          data,
          cell: (colProps) => <CreatedBy colProps={colProps} />,
        };
        break;
      case "createdAt":
        columnObject = {
          ...column,
          columnMenu: { ...columnMenu },
          headerCell: (e) => headerCell({ ...e, headerClassName }),
          data,
          cell: ({ dataItem }) => (
            <UserGeneric
              title={moment(get(dataItem, "createdAt", "")).format(
                KENDO_DATE_FORMAT
              )}
              addtionalClass="text-center"
            />
          ),
        };
        break;
      case "assignedToTargets":
        columnObject = {
          ...column,
          data,
          headerCell: (e) => headerCell({ ...e, headerClassName }),
          cell: (colProps) => <AssignedToTargets colProps={colProps} />,
        };
        break;
      case "action":
        columnObject = {
          ...column,
          data,
          headerCell: (e) => headerCell({ ...e, headerClassName }),
          columnMenu: (props) => (
            <CustomColumnSelector
              {...props}
              columns={orderBy(
                myCheckinTemplateColumns.map((c) => {
                  const temp = { ...c };
                  temp.show = get(selectedFilter, "columns", []).includes(
                    c.key
                  );
                  return temp;
                }),
                ["srNo"],
                ["asc"]
              )}
              onCloseMenu={() => {}}
              onColumnsSubmit={(cols) => {
                setColumnsState(cols);
                onColumnsSubmit(
                  cols,
                  gridFilters,
                  setGridFilters
                  // setKendoGridView
                );
              }}
              data={data}
            />
          ),
          cell: ({ dataItem }) => (
            <td>
              <Box display={"flex"} justifyContent="center">
                <GenericActionPopover
                  menuList={menuList({ dataItem }, setOpenPreview)}
                />
              </Box>
            </td>
          ),
        };
        break;
      default:
        columnObject = { ...column };
        break;
    }
    if (column.field === "action") {
      return <Column {...columnObject} width={columnObject.minWidth} />;
    } else {
      return (
        <Column
          {...columnObject}
          {...columnMenu}
          width={columnObject.minWidth}
        />
      );
    }
  };

  const formatMyCheckinTemplate = (checkins) => {
    return checkins.map((checkin) => {
      const createdDate = moment(get(checkin, "createdAt", "")).format(
        "YYYY/MM/DD"
      );
      const dateString = new Date(createdDate);
      dateString.setHours(0, 0, 0);

      return {
        id: get(checkin, "id", ""),
        title: get(checkin, "name", ""),
        checkInType: CHECKIN_TYPE[get(checkin, "checkInType", "")],
        originalCheckInType: get(checkin, "checkInType", ""),
        fullName: `${get(checkin, "createdBy.firstName", "")} ${get(
          checkin,
          "createdBy.lastName",
          ""
        )}`,
        createdByUserId: get(checkin, "createdByUserId", ""),
        createdByUser: get(checkin, "createdBy", ""),
        createdAt: dateString,
        assignedToTargets: (get(checkin, "assignedToTargets", []) || []).length,
        assignedToTargetsDetails: get(checkin, "assignedToTargets", []),
      };
    });
  };

  const [getCheckinLibrary] = useLazyQuery(checkinTemplates, {
    fetchPolicy: FETCH_POLICY.networkOnly,
    nextFetchPolicy: FETCH_POLICY.cacheOnly,
    onCompleted: (data) => {
      setMyCheckinTemplate({
        loader: false,
        checkins: get(data, "getCheckinTemplateList", []),
      });
    },
  });

  const [previewCheckin] = useLazyQuery(previewCheckinTemplates, {
    fetchPolicy: FETCH_POLICY.networkOnly,
    nextFetchPolicy: FETCH_POLICY.cacheOnly,
    onCompleted: (data) => {
      setPreviewCheckinTemplate({ ...get(data, "getCheckIn", {}) });
    },
  });

  const refetchCheckinLibrary = () => {
    setMyCheckinTemplate((data) => {
      return {
        checkins: [],
        loader: true,
      };
    });
    getCheckinLibrary({
      variables: {
        filter: get(selectedFilter, "filterKey", ""),
        listType: "CheckinTemplateList",
      },
    });
  };

  const initialData = {
    userId: getUserId(),
    companyId: getCompanyId(),
    dataState: { take: TABLE_PAGESIZE, skip: 0 },
    columns: getGridColumns(myCheckinTemplateColumns),
  };

  const initialSaveGrid = {
    ...initialData,
    gridName: "Checkin_Template",
  };

  return {
    myCheckinTemplateColumns,
    formatMyCheckinTemplate,
    individualFetchColumn,
    getCheckinLibrary,
    refetchCheckinLibrary,
    initialSaveGrid,
    previewCheckin,
    menuList,
    onColumnsSubmit,
    editActionHandler,
    deleteActionHandler,
    duplicateActionHandler,
  };
};
